import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parse } from 'date-fns';
import { Field, FieldArray, Form, Formik } from 'formik';
import { produce } from 'immer';
import _ from 'lodash';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import StatusChip from '@sophocles/components/StatusChip';
// eslint-disable-next-line no-restricted-imports -- genuine warning (trip-charge confirm), not a delete
import WarningDialog from '@sophocles/components/WarningDialog';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

import ImageTable from './ImageTable';
import PanelSubForm from './PanelSubForm';
import ServiceTable from './ServiceTable';
import { defaultPanelInstallationService } from './panelServiceDefaults';

function generateInitialValues(site, jobInfo, jobConfiguration, crews, serviceOptions, user) {
  const userId = user.get('id');
  const panelConfigurationSpecifics = _.get(jobConfiguration, 'relay_specifics', []);

  const firstCrew = crews.find(crew => crew.users.some(u => u.id === userId)) || null;

  const relays = _.get(site, ['relay_controller', 'relays'], []);
  const panels = relays
    .map(relay =>
      _.get(relay, 'loads', []).map(panel => {
        return { ...panel, relay_number: relay.relay_number };
      }),
    )
    .filter(panel => panel.length > 0);

  const panelInfo =
    jobInfo && _.get(jobInfo, 'panel_job_info', []).length > 0
      ? _.get(jobInfo, 'panel_job_info', [])
      : panels.map(panel => {
          const panelDatum = panel[0];
          const panelData = {
            installation_service: defaultPanelInstallationService(serviceOptions),
            panel_name: _.get(panelDatum, 'name', ''),
            relay_load: _.get(panelDatum, 'id', null),
            existing_fixtures_per_load: _.get(panelDatum, 'number_of_fixtures', ''),
            direction: _.get(panelDatum, 'direction', '') == '' ? 'N' : _.get(panelDatum, 'direction').trim(),
            board_type: _.get(panelDatum, 'board_type', 'Bulletin'),
            ads: _.get(panelDatum, 'ads', ''),
            new_fixtures_per_load: _.get(panelDatum, 'new_fixtures_per_load', ''),
            new_fixture_type: _.get(panelDatum, 'new_fixture_type', '17'),
            is_live: _.get(panelDatum, 'is_live', true),
            relay_number: _.get(panelDatum, 'relay_number', 1),
            delete: false,
          };
          panelData.specifics = panelConfigurationSpecifics.reduce((p, c) => {
            if (!_.has(p, c.field)) {
              p[c.field] = c.default != null ? c.default : '';
            }
            return p;
          }, {});
          return panelData;
        });

  const initialValues = jobConfiguration.specifics.reduce(
    (p, c) => {
      if (_.has(jobInfo, ['specifics', c.field])) {
        p.specifics[c.field] = _.get(jobInfo, ['specifics', c.field]);
      } else {
        p.specifics[c.field] = c.default != null ? c.default : '';
      }
      return p;
    },
    { specifics: {} },
  );

  return produce(initialValues, draft => {
    draft.submitType = 0;
    draft.installation_date = parse(
      _.get(jobInfo, 'installation_date', format(new Date(), 'yyyy-MM-dd')),
      'yyyy-MM-dd',
      new Date(),
    );
    draft.crew_id = _.get(
      jobInfo,
      ['crew', 'id'],
      !jobInfo ? _.get(jobInfo, ['crew', 'id'], '') : firstCrew ? _.get(firstCrew, 'id', '') : '',
    );
    draft.latitude_entered = _.get(jobInfo, 'latitude_entered', '');
    draft.longitude_entered = _.get(jobInfo, 'longitude_entered', '');
    draft.utility_company = _.get(jobInfo, 'utility_company', _.get(site, ['configuration', 'utility_company']));
    draft.utility_meter_number = _.get(
      jobInfo,
      'utility_meter_number',
      _.get(site, ['configuration', 'utility_meter_number']),
    );
    draft.notes = _.get(jobInfo, 'notes', '');
    draft.panel_job_info = panelInfo;
    return draft;
  });
}

function WorkForm({
  site,
  jobInfo,
  jobConfiguration,
  crews,
  images,
  services,
  servicesLoading,
  serviceOptions,
  lightFixtures,
  canConfirm,
  canDelete,
  onApprove,
  onRevert,
  onCreate,
  onUpdate,
  onCalculate,
  onDelete,
}) {
  const { user } = useUser();
  const { t } = useTranslation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();
  const [initialValues, setInitialValues] = useState(
    generateInitialValues(site, jobInfo, jobConfiguration, crews, serviceOptions, user),
  );
  const [hasMeter, setHasMeter] = useState(true);
  const [showTripChargeDialog, setShowTripChargeDialog] = useState(false);
  const [selectValues, setSelectValues] = useState(
    _.get(jobConfiguration, 'specifics', [])
      .filter(specific => specific.type === 'select')
      .reduce((p, c) => {
        if (_.has(jobInfo, ['specifics', c.field])) {
          p[c.field] = {
            [jobInfo.specifics[c.field]]: true,
          };
        }
        return p;
      }, {}),
  );

  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const isLargeDesktop = useMediaQuery(theme => theme.breakpoints.up('xl'));

  const isJobEmpty = !jobInfo || _.get(jobInfo, 'id', null) == null;
  const isPending = _.get(jobInfo, 'pending', true);

  const jobConfigurationSpecifics = _.get(jobConfiguration, 'specifics', []);
  const panelConfigurationSpecifics = _.get(jobConfiguration, 'relay_specifics', []);

  const relays = _.get(site, ['relay_controller', 'relays'], []);

  const handleSubmitForm = values => {
    const jobId = _.get(jobInfo, 'id', 0);

    const { submitType } = values;

    const payload = produce(values, draft => {
      delete draft.submitType;

      if (!hasMeter) {
        draft.utility_meter_number = 'NO METER';
        draft.utility_company = 'NO METER';
      }

      draft.crew_id = parseInt(values.crew_id);
      draft.installation_date = format(values.installation_date, 'yyyy-MM-dd');
      draft.site_id = _.get(site, 'id', 0);

      // if (draft.specifics.trip_charge) {
      //   draft.panel_job_info = [];
      // }

      draft.panel_job_info.forEach((panel, index) => {
        draft.panel_job_info[index].new_fixture_type = parseInt(panel.new_fixture_type);
      });

      jobConfiguration.specifics
        .filter(specific => specific.type === 'select')
        .forEach(specific => {
          specific.options.forEach(opt => {
            delete draft.specifics[opt.id];
          });
        });

      Object.keys(selectValues).forEach(key => {
        Object.keys(selectValues[key]).forEach(subKey => {
          draft.specifics[subKey] = selectValues[key][subKey];
        });
      });

      return draft;
    });

    if (jobId) {
      if (submitType == 1) {
        return onApprove(payload);
      }
      if (submitType == 2) {
        return onRevert(payload);
      }
      if (submitType == 3) {
        return onCalculate(payload);
      }
      return onUpdate(payload);
    }
    return onCreate(payload);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={yup.object().shape({
        installation_date: yup
          .string()
          .required(t('projectServices.workForm.installDateRequired', 'Install date is required')),
        crew_id: yup.number().required(t('projectServices.workForm.crewNumRequired', 'Crew # is required')),
        latitude_entered: yup.number().nullable(),
        longitude_entered: yup.number().nullable(),
        utility_company: yup.string().nullable(),
        meter_number: yup.string().nullable(),
        notes: yup.string().nullable(),
        specifics: yup.object().shape(),
        panel_job_info: yup
          .array()
          .of(
            yup.object().shape({
              relay_number: yup
                .number()
                .required(t('projectServices.workForm.relayNumberRequired', 'Relay number is required')),
              installation_service: yup
                .number()
                .nullable()
                .required(t('projectServices.workForm.serviceRequired', 'Service is required')),
              panel_name: yup
                .string()
                .required(t('projectServices.workForm.panelNameRequired', 'Panel name is required')),
              board_type: yup
                .string()
                .required(t('projectServices.workForm.panelTypeRequired', 'Panel type is required')),
              ads: yup
                .string()
                .required(t('projectServices.workForm.advertisementRequired', 'Advertisement is required')),
              new_fixtures_per_load: yup
                .number()
                .required(t('projectServices.workForm.newFixtureQtyRequired', 'New fixture qty is required')),
              new_fixture_type: yup
                .number()
                .required(t('projectServices.workForm.newFixtureTypeRequired', 'New fixture type is required')),
            }),
          )
          .nullable(),
      })}
      onSubmit={values => handleSubmitForm(values)}
    >
      {({
        values,
        submitForm,
        resetForm,
        setFieldValue,
        isSubmitting,
        touched,
        errors,
        dirty,
        isValid,
        handleChange,
      }) => (
        <Form>
          {deleteConfirmDialog}
          <Box
            sx={{
              alignItems: 'stretch',
              width: '100%',
              '> *:not(:last-child)': {
                mb: 3,
              },
            }}
          >
            <Paper
              sx={{
                mx: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                }}
              >
                <Typography variant={isDesktop ? 'h5' : 'h6'} sx={{ fontWeight: 'bold' }}>
                  {(isJobEmpty ? t('projectServices.workForm.newPrefix', 'New ') : '') +
                    t('projectServices.workForm.workFormColon', 'Work Form: ') +
                    _.get(site, 'name', '')}
                </Typography>
                <StatusChip
                  color={isPending ? 'warning' : 'ok'}
                  label={
                    <b>
                      {isPending
                        ? t('projectServices.workForm.pending', 'PENDING')
                        : t('projectServices.workForm.completed', 'COMPLETED')}
                    </b>
                  }
                />
              </Box>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  <Grid size={{ lg: 4, md: 12, xs: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('projectServices.job', 'Job')}
                      </Typography>
                      <Fragment>
                        {!_.get(values, ['specifics', 'trip_charge'], false) ? (
                          <Button
                            variant="text"
                            onClick={() => {
                              setShowTripChargeDialog(true);
                            }}
                          >
                            {t('projectServices.workForm.addTripCharge', 'Add Trip Charge')}
                          </Button>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              color="warning"
                              label={t('projectServices.workForm.tripChargeApplied', 'Trip Charge Applied')}
                            />
                            {isPending && (
                              <Button
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                                onClick={() => setFieldValue('specifics.trip_charge', false)}
                              >
                                {t('projectServices.workForm.remove', 'Remove')}
                              </Button>
                            )}
                          </Box>
                        )}
                        <WarningDialog
                          body={
                            <div>
                              {t(
                                'projectServices.workForm.tripChargeWarning',
                                'Only do this if you were unable to perform the installation.',
                              )}
                            </div>
                          }
                          open={showTripChargeDialog}
                          title={t('projectServices.workForm.confirmTripCharge', 'Confirm Trip Charge')}
                          onCancel={() => setShowTripChargeDialog(false)}
                          onConfirm={() => {
                            setFieldValue('specifics.trip_charge', true);
                            setShowTripChargeDialog(false);
                          }}
                        />
                      </Fragment>
                    </Box>

                    <Stack
                      spacing={2}
                      direction={isLargeDesktop ? 'row' : 'column'}
                      sx={{ mb: 2, '& .MuiFormControl-root': { width: '100%' } }}
                    >
                      <Field name="installation_date">
                        {({ field, form, meta }) => (
                          <DatePicker
                            value={field.value}
                            onChange={date => form.setFieldValue(field.name, date)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                label: t('projectServices.workForm.installDate', 'Install Date'),
                                error: meta.touched && Boolean(meta.error),
                                helperText: meta.touched && meta.error ? meta.error : '',
                              },
                            }}
                          />
                        )}
                      </Field>
                      <Field name="crew_id">
                        {({ field, form, meta }) => (
                          <FormControl fullWidth error={meta.touched && Boolean(meta.error)}>
                            <InputLabel>{t('projectServices.workForm.crewNum', 'Crew #')}</InputLabel>
                            <Select {...field} label={t('projectServices.workForm.crewNum', 'Crew #')}>
                              {crews.map(crew => (
                                <MenuItem key={crew.id} value={crew.id}>
                                  {crew.crew_number}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
                  </Grid>
                  <Grid size={{ lg: 4, md: 6, xs: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('projectServices.workForm.meter', 'Meter')}
                      </Typography>
                      <Button
                        variant="text"
                        color={hasMeter ? 'error' : 'primary'}
                        onClick={() => setHasMeter(!hasMeter)}
                      >
                        {hasMeter
                          ? t('projectServices.workForm.noMeter', 'No Meter')
                          : t('projectServices.workForm.addMeter', 'Add Meter')}
                      </Button>
                    </Box>
                    {hasMeter ? (
                      <Stack
                        spacing={2}
                        direction={isLargeDesktop ? 'row' : 'column'}
                        sx={{ '& .MuiFormControl-root': { width: '100%' } }}
                      >
                        <Field name="utility_company">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={t('projectServices.workForm.utilityCompany', 'Utility Company')}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BusinessIcon />
                                  </InputAdornment>
                                ),
                              }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>

                        <Field name="utility_meter_number">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={t('projectServices.workForm.meterNum', 'Meter #')}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SpeedIcon />
                                  </InputAdornment>
                                ),
                              }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>
                      </Stack>
                    ) : (
                      <Typography variant="caption">
                        {t('projectServices.workForm.noMeterHintPre', 'No meter for this jobInfo, to add one click')}{' '}
                        <b>{t('projectServices.workForm.addMeterCaps', 'ADD METER')}</b>{' '}
                        {t('projectServices.workForm.aboveWord', 'above.')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ lg: 4, md: 6, xs: 12 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {t('projectServices.workForm.location', 'Location')}
                    </Typography>
                    <Stack spacing={2} direction={isLargeDesktop ? 'row' : 'column'}>
                      <Field name="latitude_entered">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('projectServices.workForm.latitude', 'Latitude')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationOnIcon />
                                </InputAdornment>
                              ),
                            }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error ? meta.error : ''}
                          />
                        )}
                      </Field>

                      <Field name="longitude_entered">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('projectServices.workForm.longitude', 'Longitude')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationOnIcon />
                                </InputAdornment>
                              ),
                            }}
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error ? meta.error : ''}
                          />
                        )}
                      </Field>
                    </Stack>
                  </Grid>
                  {jobConfigurationSpecifics.length > 0 && (
                    <Grid size={12}>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('projectServices.workForm.specifics', 'Specifics')}
                      </Typography>
                      <Stack>
                        <Grid container spacing={1}>
                          <FieldArray
                            name="specifics"
                            render={() =>
                              jobConfigurationSpecifics
                                .filter(specific => specific.display)
                                .map(specific => {
                                  if (specific.type === 'int') {
                                    return (
                                      <Grid sx={{ mb: 2 }} key={specific.field} size={12}>
                                        <Field name={`specifics.${specific.field}`}>
                                          {({ field, meta }) => (
                                            <TextField
                                              {...field}
                                              fullWidth
                                              type="number"
                                              label={specific.label}
                                              size={isDesktop ? 'small' : 'medium'}
                                              error={meta.touched && Boolean(meta.error)}
                                              helperText={meta.touched && meta.error ? meta.error : ''}
                                            />
                                          )}
                                        </Field>
                                      </Grid>
                                    );
                                  }

                                  if (specific.type === 'select') {
                                    return (
                                      <Grid
                                        key={specific.field}
                                        size={12}
                                        sx={{ mb: 2, '& .MuiFormControl-root': { width: '100%' } }}
                                      >
                                        <Field name={`specifics.${specific.field}`}>
                                          {({ field, form, meta }) => (
                                            <FormControl
                                              fullWidth
                                              size={isDesktop ? 'small' : 'medium'}
                                              error={meta.touched && Boolean(meta.error)}
                                            >
                                              <InputLabel>{specific.label}</InputLabel>
                                              <Select
                                                {...field}
                                                label={specific.label}
                                                onChange={e => {
                                                  const newValue = e.target.value;
                                                  setSelectValues(
                                                    produce(selectValues, draft => {
                                                      if (newValue === 'none') {
                                                        delete draft[specific.field];
                                                      } else {
                                                        draft[specific.field] = { [newValue]: 1 };
                                                      }
                                                    }),
                                                  );
                                                  form.setFieldValue(field.name, newValue);
                                                }}
                                              >
                                                <MenuItem value="none">
                                                  {t('projectServices.workForm.none', 'None')}
                                                </MenuItem>
                                                {specific.options.map(opt => (
                                                  <MenuItem key={opt.id} value={opt.id}>
                                                    {opt.label}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                              {meta.touched && meta.error && (
                                                <FormHelperText>{meta.error}</FormHelperText>
                                              )}
                                            </FormControl>
                                          )}
                                        </Field>
                                      </Grid>
                                    );
                                  }

                                  if (specific.type === 'bool') {
                                    return (
                                      <Grid key={specific.field} size={{ lg: 4, md: 6, xs: 12 }}>
                                        <Field name={`specifics.${specific.field}`}>
                                          {({ field }) => (
                                            <FormControlLabel
                                              control={<Checkbox {...field} checked={field.value} />}
                                              label={specific.label}
                                            />
                                          )}
                                        </Field>
                                      </Grid>
                                    );
                                  }

                                  return null;
                                })
                            }
                          />
                        </Grid>
                        <Field name="notes">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={t('projectServices.workForm.notes', 'Notes')}
                              multiline
                              rows={6}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                              // Anchor typed text to the top of the tall filled slot instead
                              // of MUI's default vertical centering (see SettingsTextField.jsx).
                              sx={{
                                '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
                                '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
                              }}
                            />
                          )}
                        </Field>
                      </Stack>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('projectServices.workForm.panels', 'Panels')}
                      </Typography>
                      {isPending && (
                        <Button
                          variant="text"
                          onClick={() => {
                            const newPanel = {
                              is_live: false,
                              installation_service: defaultPanelInstallationService(serviceOptions),
                              panel_name: t('projectServices.workForm.newPanelName', 'New Panel {{num}}', {
                                num: values.panel_job_info.length + 1,
                              }),
                              board_type: 'Bulletin',
                              direction: '',
                              ads: '',
                              new_fixtures_per_load: '',
                              new_fixture_type: 17,
                              relay_number: 1,
                            };

                            setFieldValue(
                              'panel_job_info',
                              produce(values.panel_job_info, draft => {
                                draft.push(newPanel);
                                return draft;
                              }),
                            );

                            setInitialValues(
                              produce(initialValues, draft => {
                                draft.panels_job_info.push(newPanel);
                                return draft;
                              }),
                            );
                          }}
                        >
                          {t('projectServices.workForm.addPanel', 'Add Panel')}
                        </Button>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <FieldArray
                        name="panel_job_info"
                        render={() =>
                          values.panel_job_info.map((panel, index) =>
                            panel ? (
                              <Grid key={`panel_${index}`} size={{ lg: 6, xs: 12 }}>
                                <Paper key={index} sx={{ p: 2 }} elevation={2}>
                                  <PanelSubForm
                                    key={index}
                                    namespace="panel_job_info"
                                    panel={panel}
                                    panelIndex={index}
                                    availableRelays={Array.from({ length: relays.length }, (_, i) => i + 1)}
                                    configuration={panelConfigurationSpecifics}
                                    serviceOptions={serviceOptions}
                                    canConfirm={canConfirm}
                                    lightFixtures={lightFixtures}
                                    errors={errors}
                                    touched={touched}
                                    canUndelete={isPending && canConfirm}
                                    undeletePanel={() => {
                                      const newPanelInfo = produce(values.panel_job_info, draft => {
                                        draft[index].delete = false;
                                        return draft;
                                      });

                                      setFieldValue('panel_job_info', newPanelInfo);
                                    }}
                                    removePanel={() => {
                                      const newPanelInfo = produce(values.panel_job_info, draft => {
                                        draft.splice(index, 1);
                                        return draft;
                                      });

                                      setFieldValue('panel_job_info', newPanelInfo);
                                    }}
                                    deletePanel={() => {
                                      const newPanelInfo = produce(values.panel_job_info, draft => {
                                        draft[index].delete = true;
                                        return draft;
                                      });

                                      setFieldValue('panel_job_info', newPanelInfo);
                                    }}
                                    name={_.get(panel, 'panel_name', null)}
                                  />
                                </Paper>
                              </Grid>
                            ) : null,
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2 }}>
                      <Box>
                        {!isJobEmpty && canDelete && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() =>
                              confirmDelete(jobInfo, {
                                entityLabel: t('projectServices.job', 'Job'),
                                onConfirm: () => onDelete(),
                              })
                            }
                          >
                            {t('projectServices.deleteJob')}
                          </Button>
                        )}
                      </Box>
                      <Stack spacing={2} direction="row">
                        {isJobEmpty ? (
                          <Button
                            disabled={isSubmitting}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setFieldValue('submitType', 0);
                              submitForm();
                            }}
                          >
                            {t('projectServices.workForm.submit', 'Submit')}
                          </Button>
                        ) : isPending ? (
                          <Button
                            disabled={isSubmitting}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setFieldValue('submitType', 0);
                              submitForm();
                            }}
                          >
                            {t('projectServices.workForm.update', 'Update')}
                          </Button>
                        ) : null}

                        {!isJobEmpty && canConfirm ? (
                          isPending ? (
                            <Button
                              disabled={isSubmitting}
                              variant="contained"
                              color="success"
                              onClick={() => {
                                setFieldValue('submitType', 1);
                                submitForm();
                              }}
                            >
                              {t('projectServices.workForm.approve', 'Approve')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setFieldValue('submitType', 2);
                                submitForm();
                              }}
                            >
                              {t('projectServices.workForm.unapprove', 'Unapprove')}
                            </Button>
                          )
                        ) : null}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <Paper
              sx={{
                mx: 1,
              }}
            >
              <ImageTable title={t('projectServices.workForm.images', 'Images')} images={images} />
            </Paper>
            {!isJobEmpty && canConfirm && (
              <Paper
                sx={{
                  mx: 1,
                }}
              >
                {jobInfo && (
                  <ServiceTable
                    title={t('projectServices.workForm.services', 'Services')}
                    services={services}
                    loading={servicesLoading}
                    actions={[
                      <Button
                        key="calculate"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={() => {
                          setFieldValue('submitType', 3);
                          submitForm();
                        }}
                      >
                        {t('projectServices.workForm.calculateServices', 'Calculate Services')}
                      </Button>,
                    ]}
                  />
                )}
              </Paper>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default WorkForm;
